import React, {useCallback, useEffect, useState} from 'react'
import {Alert, Badge, Button, Form, Modal, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'
import {URL as BACKEND_URL} from '../../../gql/client'
import {STORAGE_NAMES} from '../../../helpers/consts'
import {parseId} from '../../../helpers'

interface Props {
  isOpen: boolean
  onClose: () => void
  stage: {id: string; name: string}
}

interface TagOption {
  id: string
  name: string
  color: string
}

interface WebhookConfig {
  id: number
  token: string
  webhook_secret: string
  webhook_url: string
  is_active: boolean
  type_lead: 'PERSONAL' | 'COMERCIAL'
  lead_source_id: string | null
  lead_source_name: string | null
  campus_id: string | null
  campus_name: string | null
  name_field: string
  email_field: string
  phone_field: string
  tags: TagOption[]
  last_triggered_at: string | null
  leads_count: number
}

interface LeadSourceCategory {
  id: string
  name: string
  sources: {id: string; name: string}[]
}

interface Options {
  categories: LeadSourceCategory[]
  campuses: {id: string; name: string}[]
  tags: TagOption[]
}

type CopyField = 'url' | 'secret' | null

function authHeaders(): HeadersInit {
  const token = localStorage.getItem(STORAGE_NAMES.TOKEN) || ''
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  }
}

const emdash = '\u2014'

const wM = defineMessages({
  facebookTitle: {id: 'leads.webhook.facebookTitle', defaultMessage: 'Facebook Lead Ads Webhook'},
  fbInstructions: {id: 'leads.webhook.fbInstructions', defaultMessage: 'Configure Zapier: <b>Facebook Lead Ads \u2192 Webhooks by Zapier (POST)</b>. Paste the URL and the secret into the Zap.'},
  webhookUrl: {id: 'leads.webhook.webhookUrl', defaultMessage: 'Webhook URL'},
  webhookSecret: {id: 'leads.webhook.webhookSecret', defaultMessage: 'Webhook Secret'},
  fbSecretHint: {id: 'leads.webhook.fbSecretHint', defaultMessage: 'Send this value in the <code>webhook_secret</code> field of the Zapier payload.'},
  leadsReceived: {id: 'leads.webhook.leadsReceived', defaultMessage: 'Leads received'},
  lastTriggered: {id: 'leads.webhook.lastTriggered', defaultMessage: 'Last triggered'},
  statusLabel: {id: 'common.field.status', defaultMessage: 'Status'},
  fieldMapping: {id: 'leads.webhook.fieldMapping', defaultMessage: 'Field mapping'},
  fieldMappingDesc: {id: 'leads.webhook.fieldMappingDesc', defaultMessage: 'Indicate which key of the JSON that Zapier sends corresponds to each lead field.'},
  updateSettings: {id: 'leads.webhook.updateSettings', defaultMessage: 'Update settings'},
  createForStage: {id: 'leads.webhook.createForStage', defaultMessage: 'Create webhook for this stage'},
  leadSourceCategory: {id: 'leads.webhook.leadSourceCategory', defaultMessage: 'Lead Source Category'},
  selectCategory: {id: 'leads.webhook.selectCategory', defaultMessage: '\u2014 Select category \u2014'},
  selectSource: {id: 'leads.webhook.selectSource', defaultMessage: '\u2014 Select source \u2014'},
  campus: {id: 'leads.webhook.campus', defaultMessage: 'Campus'},
  selectCampus: {id: 'leads.webhook.selectCampus', defaultMessage: '\u2014 Select campus \u2014'},
  leadType: {id: 'leads.webhook.leadType', defaultMessage: 'Lead type'},
  autoAssignTags: {id: 'leads.webhook.autoAssignTags', defaultMessage: 'Auto-assign tags'},
  tagsSelected: {id: 'leads.webhook.tagsSelected', defaultMessage: '{count, plural, one {# tag selected \u2014 applied automatically to every lead from this webhook} other {# tags selected \u2014 applied automatically to every lead from this webhook}}'},
  webhookActive: {id: 'leads.webhook.webhookActive', defaultMessage: 'Webhook active'},
  dangerZone: {id: 'leads.webhook.dangerZone', defaultMessage: 'Danger zone'},
  regenTokenSecret: {id: 'leads.webhook.regenTokenSecret', defaultMessage: 'Regenerate token & secret'},
  regenWarnFb: {id: 'leads.webhook.regenWarnFb', defaultMessage: 'This invalidates the current URL. Update Zapier.'},
  confirmRegenerate: {id: 'leads.webhook.confirmRegenerate', defaultMessage: 'Confirm regenerate'},
  deleteWebhook: {id: 'leads.webhook.deleteWebhook', defaultMessage: 'Delete webhook'},
  deleteConfirm: {id: 'leads.webhook.deleteConfirm', defaultMessage: 'Delete this webhook config?'},
  yesDelete: {id: 'leads.webhook.yesDelete', defaultMessage: 'Yes, delete'},
  saving: {id: 'leads.webhook.saving', defaultMessage: 'Saving\u2026'},
  updateWebhook: {id: 'leads.webhook.updateWebhook', defaultMessage: 'Update webhook'},
  createWebhook: {id: 'leads.webhook.createWebhook', defaultMessage: 'Create webhook'},
  errLoadConfig: {id: 'leads.webhook.errLoadConfig', defaultMessage: 'Error loading webhook config'},
  errNetworkLoad: {id: 'leads.webhook.errNetworkLoad', defaultMessage: 'Network error loading config'},
  selectLeadSourceWarn: {id: 'leads.webhook.selectLeadSourceWarn', defaultMessage: 'Please select a lead source'},
  selectCampusWarn: {id: 'leads.webhook.selectCampusWarn', defaultMessage: 'Please select a campus'},
  webhookUpdated: {id: 'leads.webhook.webhookUpdated', defaultMessage: 'Webhook updated'},
  webhookCreated: {id: 'leads.webhook.webhookCreated', defaultMessage: 'Webhook created'},
  errSaving: {id: 'leads.webhook.errSaving', defaultMessage: 'Error saving webhook'},
  errNetworkSave: {id: 'leads.webhook.errNetworkSave', defaultMessage: 'Network error saving webhook'},
  webhookDeleted: {id: 'leads.webhook.webhookDeleted', defaultMessage: 'Webhook deleted'},
  errDeleting: {id: 'leads.webhook.errDeleting', defaultMessage: 'Error deleting webhook'},
  errNetworkDelete: {id: 'leads.webhook.errNetworkDelete', defaultMessage: 'Network error deleting webhook'},
  toastRegenFb: {id: 'leads.webhook.toastRegenFb', defaultMessage: 'Token and secret regenerated \u2014 update your Zapier webhook URL'},
  errRegenToken: {id: 'leads.webhook.errRegenToken', defaultMessage: 'Error regenerating token'},
  errNetworkRegen: {id: 'leads.webhook.errNetworkRegen', defaultMessage: 'Network error regenerating token'},
  copyError: {id: 'leads.webhook.copyError', defaultMessage: 'Could not copy to clipboard'},
})

export const ModalFacebookLeadsWebhook: React.FC<Props> = ({isOpen, onClose, stage}) => {
  const intl = useIntl()

  const stageNumericId = Number(parseId(stage.id))

  const [config, setConfig] = useState<WebhookConfig | null>(null)
  const [options, setOptions] = useState<Options | null>(null)
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [loadingOptions, setLoadingOptions] = useState(false)
  const [saving, setSaving] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [regenerating, setRegenerating] = useState(false)
  const [copied, setCopied] = useState<CopyField>(null)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)

  // Form state
  const [categoryId, setCategoryId] = useState('')
  const [leadSourceId, setLeadSourceId] = useState('')
  const [campusId, setCampusId] = useState('')
  const [typeLead, setTypeLead] = useState<'PERSONAL' | 'COMERCIAL'>('PERSONAL')
  const [isActive, setIsActive] = useState(true)
  const [selectedTagIds, setSelectedTagIds] = useState<string[]>([])

  // Field mapping
  const [nameField, setNameField] = useState('full_name')
  const [emailField, setEmailField] = useState('email')
  const [phoneField, setPhoneField] = useState('phone_number')

  const loadConfig = useCallback(async () => {
    if (!stageNumericId) return
    setLoadingConfig(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/facebook-leads/webhook/${stageNumericId}/`, {
        headers: authHeaders(),
      })
      if (res.status === 404) {
        setConfig(null)
      } else if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setLeadSourceId(data.lead_source_id || '')
        setCampusId(data.campus_id || '')
        setTypeLead(data.type_lead)
        setIsActive(data.is_active)
        setSelectedTagIds(data.tags.map((t) => t.id))
        setNameField(data.name_field || 'full_name')
        setEmailField(data.email_field || 'email')
        setPhoneField(data.phone_field || 'phone_number')
      } else {
        toast.error(intl.formatMessage(wM.errLoadConfig))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkLoad))
    } finally {
      setLoadingConfig(false)
    }
  }, [stageNumericId, intl])

  const loadOptions = useCallback(async () => {
    setLoadingOptions(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/facebook-leads/webhook/options/`, {
        headers: authHeaders(),
      })
      if (res.ok) {
        const data: Options = await res.json()
        setOptions(data)
      }
    } catch {
      // non-blocking
    } finally {
      setLoadingOptions(false)
    }
  }, [])

  useEffect(() => {
    if (!isOpen) {
      setConfig(null)
      setOptions(null)
      setCategoryId('')
      setLeadSourceId('')
      setCampusId('')
      setTypeLead('PERSONAL')
      setIsActive(true)
      setSelectedTagIds([])
      setNameField('full_name')
      setEmailField('email')
      setPhoneField('phone_number')
      setConfirmDelete(false)
      setConfirmRegenerate(false)
      return
    }
    loadConfig()
    loadOptions()
  }, [isOpen, loadConfig, loadOptions])

  // Resolve category from loaded config's lead_source_id
  useEffect(() => {
    if (!config?.lead_source_id || !options) return
    for (const cat of options.categories) {
      if (cat.sources.some((s) => s.id === config.lead_source_id)) {
        setCategoryId(cat.id)
        break
      }
    }
  }, [config, options])

  const filteredSources =
    options?.categories.find((c) => c.id === categoryId)?.sources || []

  const toggleTag = (id: string) => {
    setSelectedTagIds((prev) =>
      prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]
    )
  }

  const handleSave = async () => {
    if (!leadSourceId) {
      toast.warning(intl.formatMessage(wM.selectLeadSourceWarn))
      return
    }
    if (!campusId) {
      toast.warning(intl.formatMessage(wM.selectCampusWarn))
      return
    }
    setSaving(true)
    try {
      const res = await fetch(`${BACKEND_URL}/api/facebook-leads/webhook/${stageNumericId}/`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({
          lead_source_id: leadSourceId,
          campus_id: campusId,
          type_lead: typeLead,
          is_active: isActive,
          tag_ids: selectedTagIds,
          name_field: nameField.trim() || 'full_name',
          email_field: emailField.trim() || 'email',
          phone_field: phoneField.trim() || 'phone_number',
        }),
      })
      if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setSelectedTagIds(data.tags.map((t) => t.id))
        toast.success(config ? intl.formatMessage(wM.webhookUpdated) : intl.formatMessage(wM.webhookCreated))
      } else {
        const err = await res.json()
        toast.error(err?.error || intl.formatMessage(wM.errSaving))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkSave))
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async () => {
    setDeleting(true)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/facebook-leads/webhook/${stageNumericId}/delete/`,
        {method: 'DELETE', headers: authHeaders()}
      )
      if (res.ok) {
        setConfig(null)
        setConfirmDelete(false)
        toast.success(intl.formatMessage(wM.webhookDeleted))
      } else {
        toast.error(intl.formatMessage(wM.errDeleting))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkDelete))
    } finally {
      setDeleting(false)
    }
  }

  const handleRegenerate = async () => {
    setRegenerating(true)
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/facebook-leads/webhook/${stageNumericId}/regenerate/`,
        {method: 'POST', headers: authHeaders()}
      )
      if (res.ok) {
        const data: WebhookConfig = await res.json()
        setConfig(data)
        setConfirmRegenerate(false)
        toast.success(intl.formatMessage(wM.toastRegenFb))
      } else {
        toast.error(intl.formatMessage(wM.errRegenToken))
      }
    } catch {
      toast.error(intl.formatMessage(wM.errNetworkRegen))
    } finally {
      setRegenerating(false)
    }
  }

  const copyToClipboard = async (text: string, field: CopyField) => {
    try {
      await navigator.clipboard.writeText(text)
      setCopied(field)
      setTimeout(() => setCopied(null), 2000)
    } catch {
      toast.error(intl.formatMessage(wM.copyError))
    }
  }

  const isLoading = loadingConfig || loadingOptions

  return (
    <Modal show={isOpen} onHide={onClose} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title className='d-flex align-items-center gap-2'>
          <i className='bi bi-facebook text-primary' style={{fontSize: '1.3rem'}} />
          {intl.formatMessage(wM.facebookTitle)} {emdash} {stage.name}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {isLoading ? (
          <div className='text-center py-5'>
            <Spinner animation='border' />
          </div>
        ) : (
          <>
            {config && (
              <>
                <Alert variant='info' className='py-2 mb-3'>
                  <small>
                    {intl.formatMessage(wM.fbInstructions, {b: (chunks: (string | JSX.Element)[]) => <strong>{chunks}</strong>})}
                  </small>
                </Alert>

                {/* Webhook URL */}
                <Form.Group className='mb-3'>
                  <Form.Label className='fw-bold'>{intl.formatMessage(wM.webhookUrl)}</Form.Label>
                  <div className='input-group'>
                    <Form.Control
                      type='text'
                      readOnly
                      value={config.webhook_url}
                      className='font-monospace'
                      style={{fontSize: '0.8rem'}}
                    />
                    <Button
                      variant={copied === 'url' ? 'success' : 'outline-secondary'}
                      onClick={() => copyToClipboard(config.webhook_url, 'url')}
                    >
                      <i className={`bi ${copied === 'url' ? 'bi-check-lg' : 'bi-clipboard'}`} />
                    </Button>
                  </div>
                </Form.Group>

                {/* Webhook Secret */}
                <Form.Group className='mb-4'>
                  <Form.Label className='fw-bold'>{intl.formatMessage(wM.webhookSecret)}</Form.Label>
                  <div className='input-group'>
                    <Form.Control
                      type='text'
                      readOnly
                      value={config.webhook_secret}
                      className='font-monospace'
                    />
                    <Button
                      variant={copied === 'secret' ? 'success' : 'outline-secondary'}
                      onClick={() => copyToClipboard(config.webhook_secret, 'secret')}
                    >
                      <i className={`bi ${copied === 'secret' ? 'bi-check-lg' : 'bi-clipboard'}`} />
                    </Button>
                  </div>
                  <Form.Text className='text-muted'>
                    {intl.formatMessage(wM.fbSecretHint, {code: (chunks: (string | JSX.Element)[]) => <code>{chunks}</code>})}
                  </Form.Text>
                </Form.Group>

                <hr />

                {/* Stats */}
                <div className='d-flex gap-3 mb-4'>
                  <div className='border rounded p-2 flex-fill text-center'>
                    <div className='fw-bold fs-4'>{config.leads_count}</div>
                    <small className='text-muted'>{intl.formatMessage(wM.leadsReceived)}</small>
                  </div>
                  <div className='border rounded p-2 flex-fill text-center'>
                    <div className='fw-bold' style={{fontSize: '0.85rem'}}>
                      {config.last_triggered_at
                        ? new Date(config.last_triggered_at).toLocaleString()
                        : emdash}
                    </div>
                    <small className='text-muted'>{intl.formatMessage(wM.lastTriggered)}</small>
                  </div>
                  <div className='border rounded p-2 flex-fill text-center'>
                    <Badge bg={config.is_active ? 'success' : 'secondary'} className='fs-6'>
                      {config.is_active ? intl.formatMessage({id: 'common.status.active', defaultMessage: 'Active'}) : intl.formatMessage({id: 'common.status.inactive', defaultMessage: 'Inactive'})}
                    </Badge>
                    <br />
                    <small className='text-muted'>{intl.formatMessage(wM.statusLabel)}</small>
                  </div>
                </div>

                <hr />
              </>
            )}

            {/* Field mapping */}
            <h6 className='mb-2'>
              <i className='bi bi-diagram-3 me-2' />
              {intl.formatMessage(wM.fieldMapping)}
            </h6>
            <p className='text-muted small mb-3'>
              {intl.formatMessage(wM.fieldMappingDesc)}
            </p>
            <div className='row g-3 mb-4'>
              <div className='col-md-4'>
                <Form.Group>
                  <Form.Label><FormattedMessage id='common.field.name' defaultMessage='Name' /></Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='full_name'
                    value={nameField}
                    onChange={(e) => setNameField(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className='col-md-4'>
                <Form.Group>
                  <Form.Label><FormattedMessage id='common.field.email' defaultMessage='Email' /></Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='email'
                    value={emailField}
                    onChange={(e) => setEmailField(e.target.value)}
                  />
                </Form.Group>
              </div>
              <div className='col-md-4'>
                <Form.Group>
                  <Form.Label><FormattedMessage id='common.field.phone' defaultMessage='Phone' /></Form.Label>
                  <Form.Control
                    type='text'
                    placeholder='phone_number'
                    value={phoneField}
                    onChange={(e) => setPhoneField(e.target.value)}
                  />
                </Form.Group>
              </div>
            </div>

            <hr />

            {/* Config form */}
            <h6 className='mb-3'>{config ? intl.formatMessage(wM.updateSettings) : intl.formatMessage(wM.createForStage)}</h6>

            {/* Lead Source — 2 levels */}
            <Form.Group className='mb-3'>
              <Form.Label>{intl.formatMessage(wM.leadSourceCategory)}</Form.Label>
              <Form.Select
                value={categoryId}
                onChange={(e) => {
                  setCategoryId(e.target.value)
                  setLeadSourceId('')
                }}
                disabled={loadingOptions}
              >
                <option value=''>{intl.formatMessage(wM.selectCategory)}</option>
                {options?.categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            <Form.Group className='mb-3'>
              <Form.Label><FormattedMessage id='leads.form.leadSource' defaultMessage='Lead source' /></Form.Label>
              <Form.Select
                value={leadSourceId}
                onChange={(e) => setLeadSourceId(e.target.value)}
                disabled={!categoryId || loadingOptions}
              >
                <option value=''>{intl.formatMessage(wM.selectSource)}</option>
                {filteredSources.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Campus */}
            <Form.Group className='mb-3'>
              <Form.Label>{intl.formatMessage(wM.campus)}</Form.Label>
              <Form.Select
                value={campusId}
                onChange={(e) => setCampusId(e.target.value)}
                disabled={loadingOptions}
              >
                <option value=''>{intl.formatMessage(wM.selectCampus)}</option>
                {options?.campuses.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>

            {/* Type lead */}
            <Form.Group className='mb-3'>
              <Form.Label>{intl.formatMessage(wM.leadType)}</Form.Label>
              <div className='d-flex gap-3'>
                <Form.Check
                  type='radio'
                  label={intl.formatMessage({id: 'leads.form.typePersonal', defaultMessage: 'Personal'})}
                  id='fb-type-personal'
                  checked={typeLead === 'PERSONAL'}
                  onChange={() => setTypeLead('PERSONAL')}
                />
                <Form.Check
                  type='radio'
                  label={intl.formatMessage({id: 'leads.form.typeCommercial', defaultMessage: 'Commercial'})}
                  id='fb-type-comercial'
                  checked={typeLead === 'COMERCIAL'}
                  onChange={() => setTypeLead('COMERCIAL')}
                />
              </div>
            </Form.Group>

            {/* Tags */}
            {options && options.tags.length > 0 && (
              <Form.Group className='mb-3'>
                <Form.Label>{intl.formatMessage(wM.autoAssignTags)}</Form.Label>
                <div className='d-flex flex-wrap gap-2 p-2 border rounded'>
                  {options.tags.map((tag) => {
                    const selected = selectedTagIds.includes(tag.id)
                    return (
                      <button
                        key={tag.id}
                        type='button'
                        onClick={() => toggleTag(tag.id)}
                        style={{
                          background: selected ? (tag.color || '#6c757d') : 'transparent',
                          border: `2px solid ${tag.color || '#6c757d'}`,
                          borderRadius: '20px',
                          padding: '3px 12px',
                          fontSize: '0.82rem',
                          fontWeight: selected ? 600 : 400,
                          color: selected ? '#fff' : (tag.color || '#6c757d'),
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {selected && <i className='bi bi-check me-1' />}
                        {tag.name}
                      </button>
                    )
                  })}
                </div>
                {selectedTagIds.length > 0 && (
                  <small className='text-muted mt-1 d-block'>{intl.formatMessage(wM.tagsSelected, {count: selectedTagIds.length})}</small>
                )}
              </Form.Group>
            )}

            {/* Active toggle (only when editing) */}
            {config && (
              <Form.Group className='mb-3'>
                <Form.Check
                  type='switch'
                  id='fb-webhook-active-toggle'
                  label={intl.formatMessage(wM.webhookActive)}
                  checked={isActive}
                  onChange={(e) => setIsActive(e.target.checked)}
                />
              </Form.Group>
            )}

            {/* Danger zone */}
            {config && (
              <div className='border border-warning rounded p-3 mt-4'>
                <p className='fw-bold mb-2 text-warning'>
                  <i className='bi bi-exclamation-triangle me-2' />
                  {intl.formatMessage(wM.dangerZone)}
                </p>

                {!confirmRegenerate ? (
                  <Button
                    variant='outline-warning'
                    size='sm'
                    className='me-2'
                    onClick={() => setConfirmRegenerate(true)}
                  >
                    <i className='bi bi-arrow-repeat me-1' />
                    {intl.formatMessage(wM.regenTokenSecret)}
                  </Button>
                ) : (
                  <div className='d-flex align-items-center gap-2 mb-2'>
                    <small className='text-warning'>{intl.formatMessage(wM.regenWarnFb)}</small>
                    <Button
                      variant='warning'
                      size='sm'
                      onClick={handleRegenerate}
                      disabled={regenerating}
                    >
                      {regenerating ? <Spinner size='sm' animation='border' /> : intl.formatMessage(wM.confirmRegenerate)}
                    </Button>
                    <Button variant='link' size='sm' onClick={() => setConfirmRegenerate(false)}>
                      <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                    </Button>
                  </div>
                )}

                {!confirmDelete ? (
                  <Button variant='outline-danger' size='sm' onClick={() => setConfirmDelete(true)}>
                    <i className='bi bi-trash me-1' />
                    {intl.formatMessage(wM.deleteWebhook)}
                  </Button>
                ) : (
                  <div className='d-flex align-items-center gap-2'>
                    <small className='text-danger'>{intl.formatMessage(wM.deleteConfirm)}</small>
                    <Button variant='danger' size='sm' onClick={handleDelete} disabled={deleting}>
                      {deleting ? <Spinner size='sm' animation='border' /> : intl.formatMessage(wM.yesDelete)}
                    </Button>
                    <Button variant='link' size='sm' onClick={() => setConfirmDelete(false)}>
                      <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
                    </Button>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant='secondary' onClick={onClose}>
          <FormattedMessage id='common.action.close' defaultMessage='Close' />
        </Button>
        {!isLoading && (
          <Button variant='primary' onClick={handleSave} disabled={saving}>
            {saving ? (
              <>
                <Spinner size='sm' animation='border' className='me-2' />
                {intl.formatMessage(wM.saving)}
              </>
            ) : config ? (
              intl.formatMessage(wM.updateWebhook)
            ) : (
              intl.formatMessage(wM.createWebhook)
            )}
          </Button>
        )}
      </Modal.Footer>
    </Modal>
  )
}
